import { DataTable } from "../../Components/ui/table";
import { DataTableColumn } from "../../Components/ui/table/types";
import { PageLayout } from "../../Components/PageLayout";

import { usePaginatedQuery } from "../../Hooks/use-paginated-query";
import { CursorPagination } from "../../Components/ui/table/pagination";
import { FiltersWrapper } from "../../Components/ui/table/filters";
import { InputFilter } from "../../Components/ui/table/filters/input";

import { useMemo, useState } from "react";
import { StatusBadge } from "../../Components/ui/statusBadge";
import { useNavigate } from "react-router-dom";

import { Button } from "../../Components/ui/button";
import { SelectFilter } from "../../Components/ui/table/filters/select/select-filter";

type UserRow = {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  is_active: boolean;
};

const columns: DataTableColumn<UserRow>[] = [
  {
    header: "Full Name",
    labelKey: "name",
    accessor: (row: UserRow) => `${row.first_name} ${row.last_name}`,
    sortable: true,
  },
  {
    header: "Username",
    labelKey: "username",
    field: "username",
    sortable: true,
  },
  {
    header: "Email",
    labelKey: "email",
    field: "email",
    sortable: true,
  },
  {
    header: "Status",
    labelKey: "status",
    cell: (row: UserRow) => {
      const isActive = row.is_active;
      return (
        <StatusBadge
          variant={isActive ? "positive" : "negative"}
          labelKey={isActive ? "active" : "inactive"}
        />
      );
    },
  },
];

const initialUrl = "/api/users/";

const UsersRoute = () => {
  const [name, setName] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [status, setStatus] = useState<string>("active");
  const navigate = useNavigate();

  const params = useMemo(
    () => ({
      is_active: status,
      name: name,
      username: username,
      email: email,
    }),
    [name, username, email, status],
  );

  const result = usePaginatedQuery<UserRow>({
    initialUrl: initialUrl,
    params: params,
  });

  return (
    <PageLayout title="Users" header="users">
      <FiltersWrapper>
        <InputFilter value={name} onChange={setName} placeholder="name" />
        <InputFilter
          value={username}
          onChange={setUsername}
          placeholder="username"
        />
        <InputFilter value={email} onChange={setEmail} placeholder="email" />
        <SelectFilter
          value={status}
          onChange={setStatus}
          options={[
            { optionLabel: "Active", optionValue: "active" },
            { optionLabel: "Inactive", optionValue: "inactive" },
          ]}
        />
        <Button
          variant="positive"
          labelKey="create"
          onClick={() => navigate("/users/create")}
        />
      </FiltersWrapper>
      <CursorPagination
        hasNext={result.hasNext}
        hasPrevious={result.hasPrevious}
        currentPage={result.currentPage}
        fetchNextPage={result.fetchNextPage}
        fetchPreviousPage={result.fetchPreviousPage}
      />
      <DataTable
        columns={columns}
        data={result.results}
        isLoading={result.isLoading}
        onRowClick={(row) => navigate(`/users/${row.id}`)}
        getRowKey={(row) => row.id}
      />
    </PageLayout>
  );
};

export default UsersRoute;
